import React from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '@/shared/ui';
import type { GroupAggregate } from '../../pages/property-grouping';

/** The three transaction segments, in render order, with their caption token. */
const SEGMENTS: { key: keyof GroupAggregate['byBusinessType']; color: string }[] = [
  { key: 'sale', color: 'var(--seed-base)' },
  { key: 'rental', color: 'var(--accent-petroleo)' },
  { key: 'both', color: 'var(--accent-ambar)' },
];

interface GroupSplitBarProps {
  /** The group's transaction-kind counts. */
  split: GroupAggregate['byBusinessType'];
  /** `inline` renders a one-line caption (group header); `bar` renders the
   *  filled track plus a legend (summary card). */
  variant?: 'inline' | 'bar';
}

/**
 * Renders the sale / rental / both split for a group.
 *
 * The caption variant is the dense one-liner used in the group header
 * ("5 VENDA · 3 LOCAÇÃO · 1 AMBOS"); the bar variant is the filled track with
 * a legend used on the summary card. Both key off the `BusinessTypeBadge`
 * caption colours so the three kinds stay tellable apart everywhere.
 *
 * @param props - The split counts and the chosen variant.
 * @returns The split element, or null when the group is empty.
 */
export const GroupSplitBar: React.FC<GroupSplitBarProps> = ({ split, variant = 'inline' }) => {
  const { t } = useTranslation('features/properties');
  const total = split.sale + split.rental + split.both;
  if (total === 0) return null;

  const present = SEGMENTS.filter(({ key }) => split[key] > 0);

  if (variant === 'inline') {
    return (
      <div
        data-testid="group-split-inline"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 'var(--space-1-5)',
          fontSize: 'var(--text-tag)',
          letterSpacing: 'var(--tracking-chip, 0.06em)',
          textTransform: 'uppercase',
          fontWeight: 'var(--font-semibold)' as unknown as number,
          lineHeight: 1.2,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {present.map(({ key, color }, index) => (
          <React.Fragment key={key}>
            {index > 0 && <Text component="span" style={{ color: 'var(--text-tertiary)' }}>·</Text>}
            <Text component="span" style={{ color }}>
              {t('list.grouping.splitItem', { count: split[key], type: t(`businessType.${key}`) })}
            </Text>
          </React.Fragment>
        ))}
      </div>
    );
  }

  return (
    <div data-testid="group-split-bar" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1-5)' }}>
      <div
        aria-hidden="true"
        style={{
          display: 'flex',
          height: 6,
          borderRadius: 'var(--radius-full)',
          overflow: 'hidden',
          background: 'var(--surface-tertiary)',
        }}
      >
        {present.map(({ key, color }) => (
          <Text
            component="span"
            key={key}
            style={{ flex: `${split[key]} 0 0`, background: color }}
          />
        ))}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
        {present.map(({ key, color }) => (
          <Text
            component="span"
            key={key}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-1)',
              fontSize: 'var(--text-tag)',
              letterSpacing: 'var(--tracking-chip, 0.06em)',
              textTransform: 'uppercase',
              fontWeight: 'var(--font-semibold)' as unknown as number,
              color,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            <Text
              component="span"
              aria-hidden="true"
              style={{ width: 8, height: 8, borderRadius: 'var(--radius-full)', background: color }}
            />
            <Text as="span">
              {t('list.grouping.splitItem', { count: split[key], type: t(`businessType.${key}`) })}
            </Text>
          </Text>
        ))}
      </div>
    </div>
  );
};
